import { useEffect, useState } from 'react'
import { listAssessments } from '~/api/assessments'
import type { AdminAssessment } from '~/api/types'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
    DataListTable,
    type DataListColumn,
} from '~/components/ui/data-list-table'
import { SearchInput } from '~/components/ui/search-input'
import { Select } from '~/components/ui/select'
import { formatDateTime, whoLabel } from '~/lib/utils'

// 인증평가 목록 테이블. 상태 필터 + limit/offset 페이지네이션 + 행 클릭 콜백.
const PAGE_SIZE = 20

const STATUS_OPTIONS = [
    { value: '', label: '전체 상태' },
    { value: 'collecting', label: '수집 중' },
    { value: 'scored', label: '채점 완료' },
    { value: 'reported', label: '보고서 발행' },
]

export const STATUS_TONES: Record<string, BadgeTone> = {
    collecting: 'warning',
    scored: 'info',
    reported: 'success',
}

const COLUMNS: DataListColumn<AdminAssessment>[] = [
    {
        key: 'title',
        header: '제목',
        className: 'font-bold',
        render: (a) => a.title,
    },
    { key: 'scale', header: '규모', render: (a) => a.scale ?? '-' },
    { key: 'industry', header: '업종', render: (a) => a.industry ?? '-' },
    {
        key: 'classification',
        header: '분류',
        render: (a) => a.classification ?? '-',
    },
    {
        key: 'assessmentType',
        header: '단계',
        render: (a) => a.assessmentType ?? '-',
    },
    {
        key: 'owner',
        header: '사용자',
        className: 'max-w-[180px] truncate',
        title: (a) => a.ownerEmail ?? undefined,
        render: (a) => whoLabel(a.ownerName, a.ownerEmail),
    },
    {
        key: 'status',
        header: '상태',
        render: (a) => (
            <Badge tone={STATUS_TONES[a.status] ?? 'neutral'}>{a.status}</Badge>
        ),
    },
    {
        key: 'score',
        header: '점수',
        className: 'whitespace-nowrap',
        render: (a) =>
            `${a.totalScore ?? '-'}${a.maxScore != null ? ` / ${a.maxScore}` : ''}`,
    },
    {
        key: 'createdAt',
        header: '생성일',
        render: (a) => formatDateTime(a.createdAt),
    },
]

export function AssessmentTable({
    onSelect,
    selectedId,
}: {
    onSelect?: (assessment: AdminAssessment) => void
    selectedId?: string
}) {
    const [items, setItems] = useState<AdminAssessment[]>([])
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [status, setStatus] = useState('')
    const [q, setQ] = useState('')
    const [offset, setOffset] = useState(0)

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        listAssessments({
            status: status || undefined,
            q: q.trim() || undefined,
            limit: PAGE_SIZE,
            offset,
        })
            .then((data) => {
                if (alive) setItems(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [status, q, offset])

    return (
        <div>
            <div className='mb-4 flex flex-wrap items-end gap-3'>
                <label className='flex flex-col gap-1'>
                    <span className='krds-label'>상태</span>
                    <Select
                        className='h-9 w-40'
                        value={status}
                        onChange={(e) => {
                            setOffset(0)
                            setStatus(e.target.value)
                        }}>
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </Select>
                </label>
                <label className='flex flex-col gap-1'>
                    <span className='krds-label'>검색</span>
                    <SearchInput
                        className='w-72'
                        value={q}
                        onChange={(v) => {
                            setOffset(0)
                            setQ(v)
                        }}
                        placeholder='제목·사용자 검색'
                    />
                </label>
            </div>

            <DataListTable
                items={items}
                loading={loading}
                error={error}
                columns={COLUMNS}
                getRowId={(a) => a.id}
                onRowClick={onSelect}
                selectedId={selectedId}
                empty='평가가 없어요.'
            />

            <div className='mt-4 flex items-center justify-between'>
                <span className='text-muted-foreground text-xs'>
                    {offset + 1}–{offset + items.length}
                </span>
                <div className='flex gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={offset === 0}
                        onClick={() =>
                            setOffset((o) => Math.max(0, o - PAGE_SIZE))
                        }>
                        이전
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={items.length < PAGE_SIZE}
                        onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                        다음
                    </Button>
                </div>
            </div>
        </div>
    )
}
